import { ReactNode, useEffect, useState } from "react";
import { Card, Pagination, Spin } from "antd";
import { cardGridPaginationStyle, cardGridStyle, fullWidthStyle } from "../constants/style";
import useContextMenu from "../hooks/use-context-menu";
import Paragraph from "antd/es/typography/Paragraph";

const DEFAULT_PAGE_SIZE = 12;

type Props<DataType> = {
  rows: DataType[],
  loading?: boolean,
  disablePagination?: boolean,
  pageSize?: number,
  emptyText?: string,
  renderCard: (row: DataType) => ReactNode,
  rowKey?: (row: DataType, index: number) => string | number,
  onSelect?: (r: DataType) => void,
  onContextMenu?: (r: DataType) => string[],
  onContextMenuOptionSelected?: (r: DataType, option: string) => void
};

export default function <DataType>(p: Props<DataType>) {
  const pageSize = p.pageSize ?? DEFAULT_PAGE_SIZE;
  const rowKey = p.rowKey ?? ((row: DataType, index: number) => (row as { key?: string | number })?.key ?? index);
  const [page, setPage] = useState(1);
  const { open, overlay } = useContextMenu<DataType>(p.onContextMenuOptionSelected);

  useEffect(() => {
    setPage(1);
  }, [p.rows.length]);

  const startIndex = (page - 1) * pageSize;
  const pagedRows = p.disablePagination ? p.rows : p.rows.slice(startIndex, startIndex + pageSize);
  const showPagination = !p.disablePagination && p.rows.length > pageSize;
  const isEmpty = !p.loading && p.rows.length === 0;

  return (
    <div style={fullWidthStyle}>
      <div style={{ position: "relative" }}>
        <Spin spinning={p.loading ?? false}>
          {
            isEmpty ?
              <Paragraph>{p.emptyText ?? "There is no data to show."}</Paragraph> :
              <div style={cardGridStyle}>
                {
                  pagedRows.map((row, index) => (
                    <Card
                      key={rowKey(row, index)}
                      hoverable={!!p.onSelect}
                      style={p.onSelect ? { cursor: "pointer" } : undefined}
                      onClick={() => p.onSelect?.(row)}
                      onContextMenu={!p.onContextMenu ? undefined : (mouseEvent) => {
                        mouseEvent.preventDefault();
                        const options = p.onContextMenu!(row);
                        if (!options) {
                          return;
                        }

                        open(mouseEvent, row, options);
                      }}
                    >
                      {p.renderCard(row)}
                    </Card>
                  ))
                }
              </div>
          }
        </Spin>
        {overlay}
      </div>
      {
        !showPagination ? undefined :
          <div style={cardGridPaginationStyle}>
            <Pagination
              current={page}
              pageSize={pageSize}
              total={p.rows.length}
              onChange={setPage}
              showSizeChanger={false}
            />
          </div>
      }
    </div>
  );
}
